import * as Sentry from "@sentry/react";

import { APP_API_BASE_URL } from "@/data_source/constants/env";
import { ErrorNotificationStore } from "@/stores/error-store";
import { ProjectSelectedStore } from "@/stores/project-selected-store";
import { WorkspaceContextStore } from "@/stores/workspace-context-store";

import { RequestCacheBuilder } from "./cache";
import {
  assetDisplayRequestScope,
  getAssetDisplayRevision,
  guardAssetDisplayResponse
} from "./asset-display-refresh";
import { readJsonResponse } from "./readJsonResponse";
import { RequestError } from "./RequestError";
import { urlWorkspaceQueryWriter } from "./workspaceQuery";
import {
  advanceWorkOrderVisibilityGeneration,
  getWorkOrderVisibilityGeneration
} from "./work-order-visibility-context";

export type RequestResponse<T> = {
  headers: Headers;
  data: T;
  status?: number;
};

export type AbortableRequest<T> = {
  response: Promise<RequestResponse<T>>;
  abort: () => void;
};

const SKIP_ERROR_NOTIFICATION_HEADER = "x-skip-error-notification";
export const REQUIRE_JSON_RESPONSE_HEADER = "x-require-json-response";
export const SKIP_AUTH_EXPIRATION_HEADER = "x-skip-auth-expiration";
/**
 * Comma-separated error codes the caller explains itself because they belong
 * to a third party (e.g. an expired Google token), not to the Sitehub session.
 * An error whose JSON body carries one of these `code`s skips the global error
 * notice, and a 401 with it keeps the session; any other 401 on the same
 * request still expires it.
 */
export const SKIP_AUTH_EXPIRATION_FOR_CODES_HEADER =
  "x-skip-auth-expiration-for-codes";
/**
 * Comma-separated error codes the caller explains itself (e.g. an import
 * refusal shown in its dialog): an error with one of them skips only the
 * global notice. It never changes session handling and never applies to a
 * 401, which keeps its usual notice and expiry.
 */
export const SKIP_ERROR_NOTIFICATION_FOR_CODES_HEADER =
  "x-skip-error-notification-for-codes";

type UnauthorizedResponseHandler = () => void;

let unauthorizedResponseHandler: UnauthorizedResponseHandler | null = null;
const pendingRequestControllers = new Map<
  AbortController,
  { method: string; url: string }
>();

type InFlightGetEntry = {
  assetDisplayRevision: number;
  response: Promise<RequestResponse<unknown>>;
  hardAbort: () => void;
  subscribers: number;
};

/** Deduplicate concurrent GETs that resolve to the same cache key. */
const inFlightGetByCacheKey = new Map<string, InFlightGetEntry>();

export function registerUnauthorizedResponseHandler(
  handler: UnauthorizedResponseHandler | null
): void {
  unauthorizedResponseHandler = handler;
}

export function abortPendingRequests(): void {
  pendingRequestControllers.forEach((_, controller) => controller.abort());
  pendingRequestControllers.clear();
}

export function requestTargetsWorkOrder(
  requestUrl: string,
  workOrderPk: number
): boolean {
  const target = `${workOrderPk}`;
  const url = new URL(requestUrl, "https://sitehub.invalid");
  const isWorkOrderCollection = /\/workorders\/$/.test(url.pathname);
  return (
    url.pathname.includes(`/workorders/${target}/`) ||
    Array.from(url.searchParams.entries()).some(
      ([key, value]) =>
        (/(work.?order|pending.?issue)/i.test(key) ||
          (isWorkOrderCollection && /^(id|pk)$/i.test(key))) &&
        value.split(",").includes(target)
    )
  );
}

export function abortPendingWorkOrderRequests(workOrderPk: number): void {
  pendingRequestControllers.forEach((requestInfo, controller) => {
    if (requestInfo.method !== "GET") return;
    if (!requestTargetsWorkOrder(requestInfo.url, workOrderPk)) return;
    controller.abort();
    pendingRequestControllers.delete(controller);
  });
}

/**
 * Centralized auth expiration: a 401 to a request that carried credentials
 * clears the session. Exported for transports that must use `fetch` directly
 * (e.g. blob downloads with `redirect: "manual"`).
 */
export function notifyUnauthorizedResponse(
  status: number,
  headers: Record<string, string>,
  errorCode?: unknown
): void {
  if (
    status !== 401 ||
    !headers.Authorization ||
    headers[SKIP_AUTH_EXPIRATION_HEADER] === "true" ||
    isSessionPreservingCode(headers, errorCode)
  ) {
    return;
  }
  unauthorizedResponseHandler?.();
}

function isSessionPreservingCode(
  headers: Record<string, string>,
  errorCode: unknown
): boolean {
  return isListedCode(
    headers[SKIP_AUTH_EXPIRATION_FOR_CODES_HEADER],
    errorCode
  );
}

/** A code as the server sent it: a string or DRF's one-item list. */
function singleCode(errorCode: unknown): unknown {
  return Array.isArray(errorCode) && errorCode.length === 1
    ? errorCode[0]
    : errorCode;
}

function isListedCode(listed: string | undefined, errorCode: unknown): boolean {
  if (typeof errorCode !== "string" || !errorCode) return false;
  return (listed ?? "").split(",").some((code) => code.trim() === errorCode);
}

/**
 * Whether the caller explains this error itself, so the global notice would
 * only repeat it. A 401 only counts when it keeps the session (a string code
 * of `x-skip-auth-expiration-for-codes`): an expiring session keeps its
 * notice. Other statuses also accept DRF's one-item list and the notice-only
 * codes of `x-skip-error-notification-for-codes`.
 */
function isCallerExplainedError(
  status: number,
  headers: Record<string, string>,
  errorCode: unknown
): boolean {
  if (status === 401) return isSessionPreservingCode(headers, errorCode);
  const code = singleCode(errorCode);
  return (
    isSessionPreservingCode(headers, code) ||
    isListedCode(headers[SKIP_ERROR_NOTIFICATION_FOR_CODES_HEADER], code)
  );
}

/** The `code` of an error body, read only when the caller opted into codes. */
async function readOptedInErrorCode(
  response: Response,
  headers: Record<string, string>
): Promise<unknown> {
  if (
    response.ok ||
    (!headers[SKIP_AUTH_EXPIRATION_FOR_CODES_HEADER] &&
      !headers[SKIP_ERROR_NOTIFICATION_FOR_CODES_HEADER])
  ) {
    return undefined;
  }
  try {
    const body: unknown = await response.clone().json();
    return typeof body === "object" && body !== null
      ? (body as { code?: unknown }).code
      : undefined;
  } catch {
    return undefined;
  }
}

// "true" silences the global error notification for any status; "5xx" only
// for server errors, so 401/403/429 keep their usual notice (#1037).
function errorNotificationSkipper(
  headers: Record<string, string>
): (status: number) => boolean {
  const value = headers[SKIP_ERROR_NOTIFICATION_HEADER];
  if (value === "true") return () => true;
  if (value === "5xx") return (status) => status >= 500;
  return () => false;
}

function stripInternalHeaders(
  headers: Record<string, string>
): Record<string, string> {
  const nextHeaders = { ...headers };
  delete nextHeaders[SKIP_ERROR_NOTIFICATION_HEADER];
  delete nextHeaders[REQUIRE_JSON_RESPONSE_HEADER];
  delete nextHeaders[SKIP_AUTH_EXPIRATION_HEADER];
  delete nextHeaders[SKIP_AUTH_EXPIRATION_FOR_CODES_HEADER];
  delete nextHeaders[SKIP_ERROR_NOTIFICATION_FOR_CODES_HEADER];
  return nextHeaders;
}

const WORKSPACE_CACHE_KEY_PARAM = "__sitehub_cache_workspace";
const VISIBILITY_CACHE_KEY_PARAM = "__sitehub_cache_visibility";

function requestTargetsWorkOrderResource(requestUrl: string): boolean {
  const url = new URL(requestUrl, "https://sitehub.invalid");
  return /\/workorders\//.test(url.pathname);
}

function requestTargetsWorkOrderCount(requestUrl: string): boolean {
  const url = new URL(requestUrl, "https://sitehub.invalid");
  return /\/workorders\/count\/?$/.test(url.pathname);
}

function requestIsGlobalWorkspaceResource(requestUrl: string | URL): boolean {
  const url = new URL(requestUrl.toString(), "https://sitehub.invalid");
  return (
    /^\/api\/workspace(?:\/|$)/.test(url.pathname) ||
    /^\/api\/users\/me(?:\/|$)/.test(url.pathname) ||
    /^\/api\/me\/workspace-context(?:\/|$)/.test(url.pathname)
  );
}

function requestIsWorkspaceBound(requestUrl: string): boolean {
  const url = new URL(requestUrl, "https://sitehub.invalid");
  if (requestIsGlobalWorkspaceResource(url)) return false;
  return (
    requestTargetsWorkOrderResource(url.toString()) ||
    /\/\d+(?:\/|$)/.test(url.pathname) ||
    url.searchParams.has("workspace__id") ||
    url.searchParams.has("workspaces__id")
  );
}

type RequestContextGuard = {
  isCurrent: () => boolean;
  staleError: () => DOMException;
};

function assertRequestContextCurrent(contextGuard?: RequestContextGuard): void {
  if (contextGuard && !contextGuard.isCurrent()) {
    throw contextGuard.staleError();
  }
}

function guardWorkOrderVisibilityResponse<T>(
  abortableRequest: AbortableRequest<T>,
  requestVisibilityGeneration: number
): AbortableRequest<T> {
  const staleVisibilityError = (): DOMException =>
    new DOMException("Work Order visibility context changed", "AbortError");
  return {
    response: abortableRequest.response
      .then((response) => {
        if (
          requestVisibilityGeneration !== getWorkOrderVisibilityGeneration()
        ) {
          throw staleVisibilityError();
        }
        return response;
      })
      .catch((error) => {
        if (
          requestVisibilityGeneration !== getWorkOrderVisibilityGeneration()
        ) {
          throw staleVisibilityError();
        }
        throw error;
      }),
    abort: abortableRequest.abort
  };
}

function guardActiveWorkspaceResponse<T>(
  abortableRequest: AbortableRequest<T>,
  requestWorkspaceId: string | number | undefined
): AbortableRequest<T> {
  const staleWorkspaceError = (): DOMException =>
    new DOMException("Workspace context changed", "AbortError");
  const isCurrent = () =>
    ProjectSelectedStore.getInstance().value?.id === requestWorkspaceId;
  return {
    response: abortableRequest.response
      .then((response) => {
        if (!isCurrent()) throw staleWorkspaceError();
        return response;
      })
      .catch((error) => {
        if (!isCurrent()) throw staleWorkspaceError();
        throw error;
      }),
    abort: abortableRequest.abort
  };
}

export function advanceWorkOrderVisibilityContext(): number {
  const generation = advanceWorkOrderVisibilityGeneration();
  RequestCacheBuilder.getInstance().deleteMatching(
    requestTargetsWorkOrderResource
  );
  return generation;
}

function workspaceScopedCacheKey(requestUrl: string): string {
  const url = new URL(requestUrl);
  if (!requestTargetsWorkOrderResource(url.toString())) return url.toString();

  const activeWorkspaceId = ProjectSelectedStore.getInstance().value?.id;
  const effectiveWorkspaceId =
    url.searchParams.get("workspaces__id") ??
    url.searchParams.get("workspace__id") ??
    activeWorkspaceId;
  if (effectiveWorkspaceId !== undefined && effectiveWorkspaceId !== null) {
    url.searchParams.set(WORKSPACE_CACHE_KEY_PARAM, `${effectiveWorkspaceId}`);
  }
  url.searchParams.set(
    VISIBILITY_CACHE_KEY_PARAM,
    `${getWorkOrderVisibilityGeneration()}`
  );
  return url.toString();
}

const UNEXPECTED_SERVER_RESPONSE_MESSAGE =
  "The server returned an unexpected response. Please try again or contact support.";

type RequestErrorBody =
  | string
  | Record<string, string | string[] | Record<string, string[]>>;

function isJsonResponse(response: Response): boolean {
  const contentType = response.headers.get("content-type") ?? "";
  return (
    contentType.includes("application/json") || contentType.includes("+json")
  );
}

function isHtmlResponseText(value: string): boolean {
  const normalizedValue = value.trim().toLowerCase();
  return (
    normalizedValue.startsWith("<!doctype") ||
    normalizedValue.startsWith("<html") ||
    normalizedValue.includes("<body")
  );
}

async function readRequestError(response: Response): Promise<RequestErrorBody> {
  if (isJsonResponse(response)) {
    try {
      const errorBody: unknown = await response.json();
      if (
        typeof errorBody === "object" &&
        errorBody !== null &&
        !Array.isArray(errorBody)
      ) {
        return errorBody as Record<
          string,
          string | string[] | Record<string, string[]>
        >;
      }
      return String(errorBody ?? response.statusText);
    } catch (_) {
      return response.statusText || UNEXPECTED_SERVER_RESPONSE_MESSAGE;
    }
  }

  const errorText = await response.text().catch(() => "");
  if (!errorText || isHtmlResponseText(errorText)) {
    return response.statusText || UNEXPECTED_SERVER_RESPONSE_MESSAGE;
  }

  return errorText;
}
function inner_request(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE") {
  return (headers: Record<string, string> = {}) =>
    (
      cache:
        | "default"
        | "no-cache"
        | "reload"
        | "force-cache"
        | "only-if-cached"
    ) =>
    (url: string) =>
    (body?: string | FormData, contextGuard?: RequestContextGuard) =>
    <T>(): AbortableRequest<T> => {
      const controller = new AbortController();
      const workspaceGenerationAtRequest =
        WorkspaceContextStore.getInstance().generation;
      pendingRequestControllers.set(controller, { method, url });
      const skipsErrorNotification = errorNotificationSkipper(headers);
      const requestHeaders = stripInternalHeaders(headers);
      return {
        response: fetch(url, {
          method,
          headers: requestHeaders,
          cache,
          body,
          mode: "cors",
          signal: controller.signal
        })
          .then(async (response) => {
            assertRequestContextCurrent(contextGuard);
            const optedInCode = await readOptedInErrorCode(response, headers);
            notifyUnauthorizedResponse(response.status, headers, optedInCode);
            // An error the caller opted into by code is its own to explain
            // (e.g. "reconnect Google"): the global notice would repeat it with
            // the raw code and, for a 401, show the not-authorized page that
            // sends the user to sign in.
            const callerOwnsError = isCallerExplainedError(
              response.status,
              headers,
              optedInCode
            );
            // Opt-in for writes whose success must be a structured JSON receipt.
            // The caller owns its contextual error notification.
            if (headers[REQUIRE_JSON_RESPONSE_HEADER] === "true") {
              try {
                const data = await readJsonResponse(response);
                if (!response.ok) {
                  throw Object.assign(
                    new RequestError(
                      response.status,
                      typeof data === "object" && data !== null
                        ? (data as Record<string, string>)
                        : String(data)
                    ),
                    { contentType: response.headers.get("Content-Type") }
                  );
                }
                return {
                  headers: response.headers,
                  data: data as T,
                  status: response.status
                };
              } catch (error) {
                Sentry.withScope((scope) => {
                  scope.setLevel("error");
                  scope.setTag("http.status_code", response.status);
                  scope.setTag("http.method", method);
                  scope.setContext("request", { url, method });
                  scope.setFingerprint([
                    `http-${response.status}`,
                    method,
                    url
                  ]);
                  Sentry.captureMessage(
                    `Request failed with status code ${response.status}`
                  );
                });
                throw error;
              }
            }
            if (response.ok) {
              if (response.status === 204) {
                return {
                  headers: response.headers,
                  data: undefined as T
                };
              }
              if (response.status >= 400) {
                Sentry.withScope((scope) => {
                  scope.setLevel("error");
                  scope.setTag("http.status_code", response.status);
                  scope.setTag("http.method", method);
                  scope.setContext("request", {
                    url,
                    method
                  });
                  scope.setFingerprint([
                    `http-${response.status}`,
                    method,
                    url
                  ]);
                  Sentry.captureMessage(
                    `Request failed with status code ${response.status}`
                  );
                });

                if (!skipsErrorNotification(response.status)) {
                  ErrorNotificationStore.getInstance().notifyByStatus(
                    response.status,
                    response.statusText
                  );
                }
                return {
                  headers: response.headers,
                  data: undefined as T
                };
              }
              try {
                const resp = await response.json();
                assertRequestContextCurrent(contextGuard);
                return {
                  headers: response.headers,
                  data: resp
                };
              } catch (error) {
                if (error instanceof Error && error.name === "AbortError") {
                  throw error;
                }
                assertRequestContextCurrent(contextGuard);
                return { headers: response.headers, data: response.body };
              }
            }
            try {
              const err = await readRequestError(response);
              assertRequestContextCurrent(contextGuard);
              const errModel = new RequestError(response.status, err);
              Sentry.withScope((scope) => {
                scope.setLevel("error");
                scope.setTag("http.status_code", response.status);
                scope.setTag("http.method", method);
                scope.setContext("request", {
                  url,
                  method
                });
                scope.setFingerprint([`http-${response.status}`, method, url]);
                Sentry.captureMessage(
                  `Request failed with status code ${response.status}`
                );
              });
              if (
                response.status !== 404 &&
                !skipsErrorNotification(response.status) &&
                !callerOwnsError
              ) {
                ErrorNotificationStore.getInstance().notifyByStatus(
                  response.status,
                  errModel.message
                );
              }
              throw errModel;
            } catch (error: unknown) {
              if (error instanceof DOMException && error.name === "AbortError")
                throw error;
              if (error instanceof RequestError) throw error;
              if (
                response.status !== 404 &&
                !skipsErrorNotification(response.status) &&
                !callerOwnsError
              ) {
                ErrorNotificationStore.getInstance().notifyByStatus(
                  response.status
                );
              }
              const errorModel = new RequestError(
                response.status,
                error instanceof Error ? error.message : String(error)
              );
              throw errorModel;
            }
          })
          .then((response) => {
            if (
              !WorkspaceContextStore.getInstance().isCurrentGeneration(
                workspaceGenerationAtRequest
              )
            ) {
              throw new DOMException(
                "Discarded response from a stale workspace context",
                "AbortError"
              );
            }
            return response;
          })
          .finally(() => pendingRequestControllers.delete(controller)),
        abort: () => controller.abort()
      };
    };
}

export function request(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE") {
  return (headers: Record<string, string> = {}) =>
    (
      cache:
        | "default"
        | "no-cache"
        | "reload"
        | "force-cache"
        | "only-if-cached"
    ) =>
    (url: string) =>
    (body?: string | FormData) =>
    <T>(): AbortableRequest<T> => {
      if (method !== "GET") {
        const abortableRequest =
          inner_request(method)(headers)(cache)(url)(body)<T>();
        return method === "POST" && requestTargetsWorkOrderCount(url)
          ? guardWorkOrderVisibilityResponse(
              abortableRequest,
              getWorkOrderVisibilityGeneration()
            )
          : abortableRequest;
      }
      const requestCache = RequestCacheBuilder.getInstance();
      const urlObj = new URL(url);
      const reloadParam = urlObj.searchParams.get("_reload");
      urlObj.searchParams.delete("_reload");
      const urlWithWorkspace = urlWorkspaceQueryWriter(urlObj);
      const cacheKey = workspaceScopedCacheKey(urlWithWorkspace);
      const displayScope = assetDisplayRequestScope(cacheKey);
      const displayRevision = getAssetDisplayRevision(displayScope);
      const requestWorkspaceId = ProjectSelectedStore.getInstance().value?.id;
      const isWorkspaceBound = requestIsWorkspaceBound(urlWithWorkspace);
      const workspaceContextGuard: RequestContextGuard | undefined =
        isWorkspaceBound
          ? {
              isCurrent: () =>
                ProjectSelectedStore.getInstance().value?.id ===
                requestWorkspaceId,
              staleError: () =>
                new DOMException("Workspace context changed", "AbortError")
            }
          : undefined;

      if (
        requestCache.has(cacheKey) &&
        cache !== "reload" &&
        cache !== "no-cache" &&
        reloadParam !== "true"
      ) {
        const cachedRequest = {
          response: Promise.resolve(
            requestCache.get(cacheKey) as RequestResponse<T>
          ),
          abort: () => {}
        };
        return guardAssetDisplayResponse(
          isWorkspaceBound
            ? guardActiveWorkspaceResponse(cachedRequest, requestWorkspaceId)
            : cachedRequest,
          displayScope,
          displayRevision
        );
      }

      // Only coalesce default GETs. Explicit reload/no-cache must hit the
      // network on their own: sharing them with an older in-flight response
      // returned stale work-order detail after mutations (#3536).
      const mayShareInFlight =
        cache !== "reload" && cache !== "no-cache" && reloadParam !== "true";
      const existingInFlight = mayShareInFlight
        ? inFlightGetByCacheKey.get(cacheKey)
        : undefined;
      if (
        existingInFlight &&
        existingInFlight.assetDisplayRevision === displayRevision
      ) {
        existingInFlight.subscribers += 1;
        const sharedRequest: AbortableRequest<T> = {
          response: existingInFlight.response as Promise<RequestResponse<T>>,
          abort: () => {
            existingInFlight.subscribers -= 1;
            // Delay hard-abort one microtask so a same-tick resubscribe
            // (useService + zip teardown/remount) can re-join the shared GET.
            queueMicrotask(() => {
              if (existingInFlight.subscribers > 0) return;
              if (inFlightGetByCacheKey.get(cacheKey) !== existingInFlight)
                return;
              inFlightGetByCacheKey.delete(cacheKey);
              existingInFlight.hardAbort();
            });
          }
        };
        return sharedRequest;
      }

      const rawRequest: AbortableRequest<T> = inner_request(method)(headers)(
        cache
      )(urlWithWorkspace)(body, workspaceContextGuard)();
      const requestVisibilityGeneration = getWorkOrderVisibilityGeneration();
      const workspaceGuardedRequest = guardAssetDisplayResponse(
        isWorkspaceBound
          ? guardActiveWorkspaceResponse(rawRequest, requestWorkspaceId)
          : rawRequest,
        displayScope,
        displayRevision
      );
      const abortableRequest: AbortableRequest<T> =
        requestTargetsWorkOrderResource(urlWithWorkspace)
          ? guardWorkOrderVisibilityResponse(
              workspaceGuardedRequest,
              requestVisibilityGeneration
            )
          : workspaceGuardedRequest;
      const inFlightEntry: InFlightGetEntry = {
        assetDisplayRevision: displayRevision,
        response: abortableRequest.response as Promise<
          RequestResponse<unknown>
        >,
        hardAbort: abortableRequest.abort,
        subscribers: 1
      };
      // Track default GETs so concurrent identical callers share one fetch.
      // Reload callers skip the map above and must not be joined later either.
      if (mayShareInFlight) {
        inFlightGetByCacheKey.set(cacheKey, inFlightEntry);
      }
      // Attach to the same promise callers await so the entry is cleared before
      // the next sequential GET in the same turn can observe a stale share.
      // Swallow the side-effect chain: callers own rejection handling on
      // `abortableRequest.response`, and an uncaught `finally` re-reject would
      // surface as an unhandled rejection in Vitest.
      void abortableRequest.response
        .then((response) => {
          if (!response.data) return;
          requestCache.set(cacheKey, response);
        })
        .catch(() => undefined)
        .finally(() => {
          if (inFlightGetByCacheKey.get(cacheKey) === inFlightEntry) {
            inFlightGetByCacheKey.delete(cacheKey);
          }
        });
      return {
        response: abortableRequest.response,
        abort: () => {
          // Non-shared GETs are not in the map; abort the controller directly.
          if (!mayShareInFlight) {
            abortableRequest.abort();
            return;
          }
          inFlightEntry.subscribers -= 1;
          queueMicrotask(() => {
            if (inFlightEntry.subscribers > 0) return;
            if (inFlightGetByCacheKey.get(cacheKey) !== inFlightEntry) return;
            inFlightGetByCacheKey.delete(cacheKey);
            abortableRequest.abort();
          });
        }
      };
    };
}

export function getRequest(
  url: string,
  headers?: Record<string, string>
): <T>() => AbortableRequest<T> {
  return request("GET")(headers)("default")(url)();
}
export function postRequest(url: string) {
  return <T>(
    data: T,
    headers?: Record<string, string>
  ): (<U>() => AbortableRequest<U>) =>
    request("POST")(headers)("no-cache")(url)(JSON.stringify(data));
}

export function putRequest(url: string, headers?: Record<string, string>) {
  return (data: Record<string, any>): (<T>() => AbortableRequest<T>) =>
    request("PUT")(headers)("no-cache")(url)(JSON.stringify(data));
}
export function deleteRequest(
  url: string,
  headers?: Record<string, string>
): <T>() => AbortableRequest<T> {
  return request("DELETE")(headers)("default")(url)();
}

export type RequestInstance = {
  getRequest: (
    url: string,
    headers?: Record<string, string>,
    cache?: "default" | "no-cache" | "reload" | "force-cache"
  ) => <T>() => AbortableRequest<T>;
  postRequest: <T, R>(
    url: string,
    data: T,
    headers?: Record<string, string>
  ) => () => AbortableRequest<R>;
  postFormDataRequest: <R>(
    url: string,
    data: FormData,
    headers?: Record<string, string>
  ) => () => AbortableRequest<R>;
  putRequest: <T, R>(
    url: string,
    data: T,
    headers?: Record<string, string>
  ) => () => AbortableRequest<R>;
  putFormDataRequest: <R>(
    url: string,
    data: FormData,
    headers?: Record<string, string>
  ) => () => AbortableRequest<R>;
  patchRequest: <T, R>(
    url: string,
    data: T,
    headers?: Record<string, string>
  ) => () => AbortableRequest<R>;
  patchFormDataRequest: <R>(
    url: string,
    data: FormData,
    headers?: Record<string, string>
  ) => () => AbortableRequest<R>;
  deleteRequest: <R>(
    url: string,
    headers?: Record<string, string>
  ) => () => AbortableRequest<R>;
  fileRequest: (
    url: string,
    headers?: Record<string, string>
  ) => () => AbortableRequest<string>;
  setGlobalHeader: (headers: Record<string, string>) => RequestInstance;
};

type RequestConfig = {
  baseUrl: string;
  headers?: Record<string, string>;
  resolveBaseUrl?: () => string;
};

export function resolveRequestHeaders(
  globalHeaders: Record<string, string> = {},
  requestHeaders?: Record<string, string>
): Record<string, string> {
  const mergedHeaders: Record<string, string> = {
    ...globalHeaders,
    ...requestHeaders,
    Authorization:
      requestHeaders?.Authorization || globalHeaders?.Authorization || ""
  };
  const token = localStorage.getItem("token");

  if (
    !token ||
    token.match(/null|undefined|bearer/i) ||
    (requestHeaders?.Authorization !== null &&
      requestHeaders?.Authorization !== undefined)
  ) {
    return mergedHeaders;
  }

  return {
    ...mergedHeaders,
    Authorization: `Bearer ${token}`
  };
}

export function createRequestInstance(
  initialConfig: RequestConfig
): RequestInstance {
  let config = { ...initialConfig };
  const baseUrl = () => initialConfig.resolveBaseUrl?.() ?? config.baseUrl;
  const instance: RequestInstance = {
    getRequest: (
      url: string,
      headers?: Record<string, string>,
      cache?: "default" | "no-cache" | "reload" | "force-cache"
    ) => {
      return request("GET")(resolveRequestHeaders(config.headers, headers))(
        cache ?? (url.match(/_reload=true/g) ? "reload" : "default")
      )(url.match("^(http|file).+") ? url : `${baseUrl()}/${url}`)();
    },
    postRequest: <T>(
      url: string,
      data: T,
      headers?: Record<string, string>
    ) => {
      return request("POST")(resolveRequestHeaders(config.headers, headers))(
        "no-cache"
      )(`${baseUrl()}/${url}`)(JSON.stringify(data));
    },
    postFormDataRequest: (
      url: string,
      data: FormData,
      headers?: Record<string, string>
    ) => {
      return request("POST")(resolveRequestHeaders(config.headers, headers))(
        "no-cache"
      )(`${baseUrl()}/${url}`)(data);
    },
    putFormDataRequest: (
      url: string,
      data: FormData,
      headers?: Record<string, string>
    ) => {
      return request("PUT")(resolveRequestHeaders(config.headers, headers))(
        "no-cache"
      )(`${baseUrl()}/${url}`)(data);
    },
    putRequest: <T>(url: string, data: T, headers?: Record<string, string>) => {
      return request("PUT")(resolveRequestHeaders(config.headers, headers))(
        "no-cache"
      )(`${baseUrl()}/${url}`)(JSON.stringify(data));
    },
    patchRequest: <T>(
      url: string,
      data: T,
      headers?: Record<string, string>
    ) => {
      return request("PATCH")(resolveRequestHeaders(config.headers, headers))(
        "no-cache"
      )(`${baseUrl()}/${url}`)(JSON.stringify(data));
    },
    patchFormDataRequest: (
      url: string,
      data: FormData,
      headers?: Record<string, string>
    ) => {
      return request("PATCH")(resolveRequestHeaders(config.headers, headers))(
        "no-cache"
      )(`${baseUrl()}/${url}`)(data);
    },
    deleteRequest: (url: string, headers?: Record<string, string>) => {
      return request("DELETE")(resolveRequestHeaders(config.headers, headers))(
        "default"
      )(`${baseUrl()}/${url}`)();
    },
    fileRequest: (url: string, headers?: Record<string, string>) => {
      const controller = new AbortController();
      const fileUrl = `${baseUrl()}/${url}`;
      const requestWorkspaceId = ProjectSelectedStore.getInstance().value?.id;
      const workspaceContextGuard: RequestContextGuard | undefined =
        requestIsWorkspaceBound(fileUrl)
          ? {
              isCurrent: () =>
                ProjectSelectedStore.getInstance().value?.id ===
                requestWorkspaceId,
              staleError: () =>
                new DOMException("Workspace context changed", "AbortError")
            }
          : undefined;
      pendingRequestControllers.set(controller, {
        method: "GET",
        url: fileUrl
      });
      const resolvedHeaders = resolveRequestHeaders(config.headers, headers);
      return () => ({
        response: fetch(fileUrl, {
          method: "GET",
          signal: controller.signal,
          // Internal flags steer this client only; sent to another origin
          // they fail the CORS preflight before the GET is made.
          headers: stripInternalHeaders(resolvedHeaders)
        })
          .then(async (response) => {
            assertRequestContextCurrent(workspaceContextGuard);
            notifyUnauthorizedResponse(response.status, resolvedHeaders);
            if (!response.ok) {
              throw new RequestError(response.status, response.statusText);
            }
            const data = await response.blob();
            assertRequestContextCurrent(workspaceContextGuard);
            return {
              headers: response.headers,
              data
            };
          })
          .then((responseBlob) => {
            assertRequestContextCurrent(workspaceContextGuard);
            return {
              ...responseBlob,
              data: URL.createObjectURL(responseBlob.data)
            };
          })
          .catch((error) => {
            if (error instanceof DOMException) throw error;
            throw new RequestError(
              error.status || 200,
              `Connection Error: ${error.message}`
            );
          })
          .finally(() => pendingRequestControllers.delete(controller)),
        // Bound: an unbound `abort` throws "Illegal invocation".
        abort: () => controller.abort()
      });
    },
    setGlobalHeader: (headers: Record<string, string>) => {
      config = { ...initialConfig, headers };
      return instance;
    }
  };
  return instance;
}

export function getRequestUrl(string: string): string | undefined {
  const match = string.match(/(api\/(\w+\/)+)/);
  return match ? match[0] : undefined;
}

export default createRequestInstance({
  baseUrl: APP_API_BASE_URL,
  resolveBaseUrl: () => APP_API_BASE_URL
});
